import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Modal from "react-modal";
import NumberFormat from "react-number-format";
import styled from "styled-components";
import { GetProfessionalsParams, TipoProfissional } from "../api/getProfessionals";
import ButtonSecondary from "../components/buttons/ButtonSecondary";
import RadioButtonField from "../components/fields/RadioButtonField";
import TextField from "../components/fields/TextField";
import { formatDate, incrementOnDate } from "../utils/date-util/dateUtils";
import "./customModal.css";

const ModalContentWrapper = styled.div`
	background: #ffffff;
	box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
	border-radius: 10px;
	position: absolute;
	left: 40px;
	right: 40px;
	top: 40px;
	bottom: 40px;
	background: #ffffff;
	padding: 15px;
	outline: none;
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;
	justify-content: flex-start;
	align-items: stretch;
	align-content: flex-start;
`;

const ModalTitle = styled.p`
	margin: 0px;
	font-family: Livvic;
	font-style: normal;
	font-weight: 600;
	font-size: 18px;
	line-height: 165.5%;
	text-align: center;
	letter-spacing: 0.005em;
	color: ${props => (props.theme.colors.blackVitat || "#002b49")};
	text-align: center;
`;

const ModalSubTitle = styled.p`
	margin: 0px;
	font-family: Livvic;
	font-style: normal;
	font-weight: 600;
	font-size: 14px;
	line-height: 165.5%;
	letter-spacing: 0.005em;
	color: ${props => (props.theme.colors.blackVitat || "#002b49")};
	margin-left: 29px;
	margin-top: 29px;
`;

const ButtonWrapper = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: flex-end;
	align-items: flex-end;
	align-content: flex-end;
	width: 100%;
	margin-top: 8px;
`;

const PersonalizedValueWrapper = styled.div`
	padding-left: 50px;
	padding-right: 50px;
	max-width: 200px;
	margin-top: 12px;
`;

const FormWrapper = styled.div`
	display: flex;
	flex-direction: column;
	overflow: auto;
`;

const RadioButtonWrapper = styled.div`
	display: flex;
	flex-direction: column;
	margin-left: 45px;
	gap: 12px;
	margin-top: 6px;
`;

interface GetProfessionalsParamsForm extends GetProfessionalsParams {
	isPricePersonalized?: boolean;
	personalizedPrice?: number;
}

interface FiltersProfessionalsModalProps {
	isOpen: boolean;
	onChange: (filters: GetProfessionalsParams) => void;
	values: GetProfessionalsParams;
}

/**
 * ### Modal de filtros do profissional
 * Dialogo de filtragem do profissional
 */
const FiltersProfessionalsModal: FC<FiltersProfessionalsModalProps> = props => {
	useEffect(() => {
		if (props.isOpen) loadData(props.values);
	}, [props.isOpen]);

	const { handleSubmit, setValue, watch, clearErrors } = useForm<GetProfessionalsParamsForm>({
		defaultValues: {
			specialty: props.values.specialty,
			tipoAtendimento: undefined,
			dataInicial: props.values.dataInicial,
			dataFinal: props.values.dataFinal,
			type: props.values.type,
			price: props.values.price,
			rating: props.values.rating,
			search: props.values.search,
			isPricePersonalized: false,
			personalizedPrice: undefined,
			tipoProfissional: undefined,
		},
	});

	const [fieldValues, setFielVelues] = useState<GetProfessionalsParams>({});

	const loadData = (values: GetProfessionalsParams) => {
		setValue("price", values.price);
		if (!(values.price == 50 || values.price == 100) && values.price != undefined) {
			setValue("isPricePersonalized", true);
			setValue("personalizedPrice", values.price);
			setValue("price", undefined);
		} else {
			setValue("isPricePersonalized", false);
			setValue("personalizedPrice", undefined);
		}
		setValue("rating", values.rating);
		setValue("dataFinal", values.dataFinal);
		setValue("tipoProfissional", values.tipoProfissional);
		setFielVelues(values);
	};

	const onChangePrice = (option: number, value: boolean) => {
		if (value) {
			setValue("isPricePersonalized", false);
			setValue("price", option);
			clearErrors("price");
		} else {
			setValue("price", undefined);
			clearErrors("price");
		}
	};

	const onChangePricePersonalized = (value: boolean) => {
		if (value) setValue("price", undefined);
		setValue("isPricePersonalized", value);

		clearErrors("price");
		clearErrors("isPricePersonalized");
	};

	const onChangeDisponibility = (option: string, value: boolean) => {
		if (value) {
			setValue("dataFinal", option.toString());
			clearErrors("dataFinal");
		} else {
			setValue("dataFinal", undefined);
			clearErrors("dataFinal");
		}
	};

	const onChangeRating = (option: string, value: boolean) => {
		if (value) {
			setValue("rating", option.toString());
			clearErrors("rating");
		} else {
			setValue("rating", undefined);
			clearErrors("rating");
		}
	};

	const onChangeTipoProfissional = (option: TipoProfissional, value: boolean) => {
		if (value) {
			setValue("tipoProfissional", option as TipoProfissional);
			clearErrors("tipoProfissional");
		} else {
			setValue("tipoProfissional", undefined);
			clearErrors("tipoProfissional");
		}
	};

	const onSubmit = handleSubmit((data: GetProfessionalsParamsForm) => {
		const registro: GetProfessionalsParamsForm = fieldValues;
		registro.price = data.price;
		if (data.isPricePersonalized) registro.price = data.personalizedPrice;
		registro.rating = data.rating;
		registro.dataFinal = data.dataFinal;
		registro.tipoProfissional = data.tipoProfissional;
		props.onChange(registro);
	});

	return (
		<Modal
			isOpen={props.isOpen}
			overlayClassName="modal-overlay-personalizado"
			contentElement={(props, children) => <ModalContentWrapper>{children}</ModalContentWrapper>}
			ariaHideApp={false}
		>
			<ModalTitle>Filtros</ModalTitle>
			<FormWrapper>
				<ModalSubTitle>Tipo do profissional</ModalSubTitle>
				<RadioButtonWrapper>
					<RadioButtonField
						value={watch("tipoProfissional") == "nutricionista"}
						onChange={(value: boolean) => onChangeTipoProfissional("nutricionista", value)}
						label="Nutrição"
					/>
				</RadioButtonWrapper>

				<ModalSubTitle>Preço</ModalSubTitle>
				<RadioButtonWrapper>
					<RadioButtonField value={watch("price") === 50} onChange={(value: boolean) => onChangePrice(50, value)} label="Até R$ 50" />
					<RadioButtonField value={watch("price") === 100} onChange={(value: boolean) => onChangePrice(100, value)} label="Até R$ 100" />
					<RadioButtonField
						value={watch("isPricePersonalized") || false}
						onChange={(value: boolean) => onChangePricePersonalized(value)}
						label="Personalizado"
					/>
				</RadioButtonWrapper>
				{watch("isPricePersonalized") && (
					<PersonalizedValueWrapper>
						{
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							<NumberFormat
								prefix="Até R$ "
								placeholder="Até R$ 0,00"
								value={watch("personalizedPrice")}
								thousandSeparator="."
								customInput={TextField}
								decimalScale={2}
								decimalSeparator=","
								max={99999999}
								inputMode="decimal"
								onValueChange={values => {
									const { value } = values;
									setValue("personalizedPrice", parseFloat(value));
									clearErrors("personalizedPrice");
								}}
							/>
						}
					</PersonalizedValueWrapper>
				)}

				<ModalSubTitle>Disponibilidade</ModalSubTitle>
				<RadioButtonWrapper>
					<RadioButtonField
						value={watch("dataFinal") === formatDate("yyyy-MM-dd")}
						onChange={(value: boolean) => onChangeDisponibility(formatDate("yyyy-MM-dd"), value)}
						label="Hoje"
					/>
					<RadioButtonField
						value={watch("dataFinal") === formatDate("yyyy-MM-dd", incrementOnDate(3, "day"))}
						onChange={(value: boolean) => onChangeDisponibility(formatDate("yyyy-MM-dd", incrementOnDate(3, "day")), value)}
						label="Próximos 3 dias"
					/>
				</RadioButtonWrapper>

				<ModalSubTitle>Nota do profissional</ModalSubTitle>
				<RadioButtonWrapper>
					<RadioButtonField value={watch("rating") == "4"} onChange={(value: boolean) => onChangeRating("4", value)} label="4 ou mais estrelas" />
					<RadioButtonField value={watch("rating") == "3"} onChange={(value: boolean) => onChangeRating("3", value)} label="3 estrelas" />
				</RadioButtonWrapper>
			</FormWrapper>

			{/* <RadioButtonField value={false} onChange={(value: boolean) => {}} label='Indiferente' /> */}
			<ButtonWrapper>
				<ButtonSecondary type="submit" label="Aplicar" onClick={onSubmit} />
			</ButtonWrapper>
		</Modal>
	);
};

export default FiltersProfessionalsModal;
